'use-client';
import * as React from 'react';
import type { ImageProps } from '@wix/thunderbolt-components-native';
import classNames from 'clsx';
import {
  formatClassNames,
  getAriaAttributes,
  getDataAttributes,
  useAnalyticsReportClicks,
} from '@wix/editor-elements-common-utils';
import Image from '@wix/thunderbolt-elements/src/components/Image/viewer/Image';
import Link, {
  isValidLink,
} from '@wix/thunderbolt-elements/src/components/Link/viewer/Link';
import { Tooltip } from '../../../common/tooltip/Tooltip';
import type { ImageButtonProps } from '../ImageButton.types';
import { TestIds, tooltipDataTestId } from '../constants';
import semanticClassNames from '../ImageButton.semanticClassNames';
import style from './style/ImageButton.scss';
import { v4 as generateId } from 'uuid';

const ImageButton: React.FC<ImageButtonProps> = props => {
  const {
    id,
    className,
    customClassNames = [],
    link,
    alt,
    a11y,
    transition,
    onClick: propsOnClick,
    onDblClick,
    onMouseEnter: _onMouseEnter,
    onMouseLeave: _onMouseLeave,
    hasPlatformClickHandler,
    isDisabled,
    getPlaceholder,
    reportBiOnClick,
    containerWidth,
    containerHeight,
    ariaAttributes,
    preventLinkNavigation,
    reducedMotion,
  } = props;
  const [showTooltip, setShowTooltip] = React.useState(false);
  const tooltipId = generateId();
  const tooltipTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isLink = isValidLink(link);
  const shouldPreventLinkNavigation = preventLinkNavigation && isLink;
  const shouldHaveOnClick =
    (!isDisabled && propsOnClick) || shouldPreventLinkNavigation;

  const images: Array<{ props: ImageProps; className: string }> = [
    { props: props.defaultImage, className: style.defaultImage },
    { props: props.hoverImage, className: style.hoverImage },
    { props: props.activeImage, className: style.activeImage },
  ];

  const showTooltipWithDelay = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 2000);
  };

  const hideTooltip = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  const handleClick = useAnalyticsReportClicks({
    reportBiOnClick,
    onClick: shouldHaveOnClick
      ? (event: React.MouseEvent) => {
          shouldPreventLinkNavigation && event.preventDefault();
          !isDisabled && propsOnClick?.(event);
        }
      : undefined,
  });

  const onMouseEnter = (event: React.MouseEvent) => {
    showTooltipWithDelay();
    if (_onMouseEnter) {
      _onMouseEnter?.(event);
    }
  };

  const onMouseLeave = (event: React.MouseEvent) => {
    hideTooltip();
    if (_onMouseLeave) {
      _onMouseLeave?.(event);
    }
  };

  const onFocus = () => {
    showTooltipWithDelay();
  };

  const onBlur = () => {
    hideTooltip();
  };

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(
        className,
        {
          [style.clickable]: hasPlatformClickHandler,
        },
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      onClick={handleClick}
      onDragStart={e => e.preventDefault()}
      onDoubleClick={!isDisabled ? onDblClick : undefined}
      onMouseEnter={!isDisabled ? onMouseEnter : undefined}
      onMouseLeave={!isDisabled ? onMouseLeave : undefined}
      onFocus={!isDisabled ? onFocus : undefined}
      onBlur={!isDisabled ? onBlur : undefined}
      aria-describedby={tooltipId}
    >
      <Link
        {...link}
        {...getAriaAttributes(ariaAttributes)}
        aria-label={a11y ? a11y?.label : alt}
        className={classNames(style.link, {
          [style.transition_none]: reducedMotion || transition === 'none',
          [style.transition_fade]: !reducedMotion && transition === 'fade',
        })}
        dataTestId={TestIds.link}
        tabIndex={0}
      >
        <div
          role="img"
          className={style.linkImageContainer}
          data-testid={TestIds.linkImageContainer}
        >
          {showTooltip && alt && (
            <Tooltip dataTestId={tooltipDataTestId} tooltipId={tooltipId}>
              {alt}
            </Tooltip>
          )}
          {images.map((image, index) => (
            <div className={style.correctPositioning} key={index}>
              <Image
                id={`img_${id}_${index}`}
                {...image.props}
                displayMode="fit"
                alt=""
                role="none"
                containerId={id}
                className={image.className}
                getPlaceholder={getPlaceholder}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
              />
            </div>
          ))}
        </div>
      </Link>
    </div>
  );
};

export default ImageButton;
